
import React, { useState, useEffect, useRef } from "react";
import { userDashboard } from "../../api/Calling";
import { FaFilter, FaSync, FaUsers, FaDownload, FaFilePdf, FaFileExcel, FaChevronDown, FaUserPlus, FaUserTag, FaLeaf, FaCalendarAlt } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { BiDollar } from "react-icons/bi";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ReactECharts from "echarts-for-react";

import formatDateTime from "../../components/formatDateTime";

// ─── Date range calculator ────────────────────────────────────────────────────
const calculateDateRange = (filters) => {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  let startDate = "";
  let endDate = formatDate(today);
  switch (filters.dateRangeType) {
    case "today": startDate = formatDate(today); break;
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = formatDate(yesterday);
      endDate = formatDate(yesterday);
      break;
    case "last15":
      const last15 = new Date(today);
      last15.setDate(last15.getDate() - 15);
      startDate = formatDate(last15);
      break;
    case "last30":
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      startDate = formatDate(last30);
      break;
    case "last6months":
      const last6months = new Date(today);
      last6months.setMonth(last6months.getMonth() - 6);
      startDate = formatDate(last6months);
      break;
    case "custom":
      startDate = filters.customStartDate;
      endDate = filters.customEndDate;
      break;
    default: startDate = formatDate(today);
  }
  return { startDate, endDate };
};

// ─── Design Tokens (Clean, Vibrant Palette) ──────────────────────────────────
const T = {
  blue: "#2563EB",   // Vibrant Main Blue
  green: "#10B981",  // Emerald Success Green
  violet: "#8B5CF6", // Organic Purple
  amber: "#F59E0B",  // PID Orange
  rose: "#E11D48",   // Vivid Red
  cyan: "#06B6D4",   // Bright Cyan
  teal: "#0D9488",   // Deep Teal
  indigo: "#4F46E5", // Deep Indigo
  orange: "#f97316", // Warm Orange
};
const PALETTE = [T.blue, T.indigo, T.green, T.amber, T.violet, T.cyan, T.teal, T.rose, T.orange];

// ─── Data builders ────────────────────────────────────────────────────────────
const buildTimelineData = (data) => {
  const map = {};
  data.forEach((u) => {
    const day = (u.createdAt || "").slice(0, 10);
    if (!day) return;
    if (!map[day]) map[day] = { date: day, total: 0, pid: 0, organic: 0 };
    map[day].total += 1;
    if (u.u_pid) {
      map[day].pid += 1;
    } else {
      map[day].organic += 1;
    }
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({ ...d, date: d.date.slice(5) }));
};

const buildTypeData = (data) => {
  const pid = data.filter(u => u.u_pid).length;
  const organic = data.filter(u => !u.u_pid).length;
  return [
    { name: "PID", value: pid },
    { name: "ORGANIC", value: organic }
  ];
};

const buildCampaignData = (data) => {
  const map = {};
  data.forEach((u) => {
    if (!u.u_pid) return; // Only count PID users for campaign stats
    const campaign = u.u_camp || "Direct";
    if (!map[campaign]) map[campaign] = { campaign, users: 0 };
    map[campaign].users += 1;
  });
  return Object.values(map).sort((a, b) => b.users - a.users).slice(0, 8);
};

const buildDailyAvgData = (data) => {
  if (data.length === 0) return { avgPerDay: 0, peakDay: null };

  const timeline = buildTimelineData(data);
  const totalDays = timeline.length;
  const totalUsers = data.length;
  const avgPerDay = totalDays > 0 ? Math.round((totalUsers / totalDays) * 10) / 10 : 0;

  const peakDay = timeline.reduce((max, day) => day.total > (max?.total || 0) ? day : max, null);

  return { avgPerDay, peakDay };
};

// ─── Animated Stat Number ─────────────────────────────────────────────────────
const AnimatedStat = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let n = 0;
    const numValue = Number(value) || 0;
    if (numValue === 0) {
      setDisplay(0);
      return;
    }
    const step = Math.max(1, Math.ceil(numValue / 30));
    const t = setInterval(() => {
      n += step;
      if (n >= numValue) { setDisplay(numValue); clearInterval(t); }
      else setDisplay(n);
    }, 18);
    return () => clearInterval(t);
  }, [value]);
  return <span>{display.toLocaleString()}</span>;
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, gradient, icon, glow }) => (
  <div style={{
    background: gradient,
    borderRadius: 20,
    padding: "22px 24px",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    boxShadow: `0 8px 32px ${glow}40, 0 1px 0 rgba(255,255,255,0.15) inset`,
    transition: "transform 0.2s, box-shadow 0.2s",
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 16px 40px ${glow}55, 0 1px 0 rgba(255,255,255,0.15) inset`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 8px 32px ${glow}40, 0 1px 0 rgba(255,255,255,0.15) inset`; }}
  >
    <div style={{ position: "absolute", top: -20, right: -20, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.1)" }} />
    <div style={{ position: "absolute", bottom: -8, right: 10, width: 50, height: 50, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
    <div style={{ position: "absolute", top: 0, left: 24, right: 24, height: 1, background: "rgba(255,255,255,0.2)" }} />
    <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div>
        <p style={{ margin: 0, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 8 }}>{label}</p>
        <p style={{ margin: 0, fontSize: 38, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}><AnimatedStat value={value} /></p>
      </div>
      <div style={{ padding: "10px 11px", background: "rgba(255,255,255,0.15)", borderRadius: 14, border: "1px solid rgba(255,255,255,0.2)", fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{icon}</div>
    </div>
  </div>
);

// ─── Chart Card Wrapper ───────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, accent = T.blue, icon, children, style = {} }) => (
  <div style={{
    background: "#fff",
    borderRadius: 24,
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 12px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
    ...style,
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
    <div style={{ position: "absolute", top: 0, right: 0, width: 280, height: 200, pointerEvents: "none", background: `radial-gradient(circle at 80% 0%, ${accent}0d 0%, transparent 60%)` }} />
    <div style={{ position: "relative", zIndex: 1, padding: "26px 28px 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20 }}>
        {icon && (
          <div style={{ padding: "9px 10px", borderRadius: 12, background: `${accent}14`, border: `1px solid ${accent}22`, fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{icon}</div>
        )}
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.02em" }}>{title}</h3>
          {subtitle && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8", fontWeight: 500, lineHeight: 1.5 }}>{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  </div>
);

// ─── Empty / Loading State ────────────────────────────────────────────────────
const ChartEmpty = ({ loading, height = 260, message = "No data for this period" }) => (
  <div style={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
    {loading ? (
      <>
        <div style={{ width: 38, height: 38, borderRadius: "50%", border: "3.5px solid #dde5ff", borderTopColor: T.blue, animation: "spin 0.8s linear infinite" }} />
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>Loading data…</p>
      </>
    ) : (
      <>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#e2e8f0,#f1f5f9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FaUsers style={{ color: "#cbd5e1", fontSize: 22 }} />
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>{message}</p>
      </>
    )}
  </div>
);

// ─── Section Label ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0" }}>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)" }} />
    <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#9ca3af", padding: "0 4px" }}>{children}</span>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)" }} />
  </div>
);

// ─── Mini Chip ────────────────────────────────────────────────────────────────
const MiniChip = ({ label, value, color, bg, border }) => (
  <div style={{
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 12,
    padding: "10px 14px",
    transition: "box-shadow 0.18s",
  }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.07)"}
    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
  >
    <p style={{ margin: 0, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>{label}</p>
    <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 900, color, lineHeight: 1 }}>{value}</p>
  </div>
);

// ─── Legend Pill ──────────────────────────────────────────────────────────────
const LegendPill = ({ color, label, icon }) => (
  <div style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 13px",
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1,
    background: color + "12",
    border: `1px solid ${color}28`,
    color: color,
  }}>
    {icon || <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 5px ${color}`, display: "inline-block" }} />}
    {label}
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// ─── ECharts option builders ──────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════

const buildAreaChartOption = (timelineData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "cross", crossStyle: { color: T.blue, width: 1.5 }, lineStyle: { color: T.blue, width: 1.5, type: "dashed" } },
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [14, 18],
    textStyle: { color: "#f1f5f9", fontSize: 13 },
    formatter: (params) => {
      const label = params[0]?.axisValue || "";
      let html = `<div style="font-size:10px;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;color:#94a3b8;margin-bottom:10px;border-bottom:1px solid rgba(255,255,255,0.07);padding-bottom:8px;">${label}</div>`;
      params.forEach(p => {
        html += `<div style="display:flex;align-items:center;gap:8px;margin-top:6px;">
          <span style="width:9px;height:9px;border-radius:50%;background:${p.color};display:inline-block;box-shadow:0 0 8px ${p.color};flex-shrink:0;"></span>
          <span style="color:#94a3b8;font-size:12px;font-weight:600;">${p.seriesName}</span>
          <span style="color:#f1f5f9;font-size:14px;font-weight:800;margin-left:auto;">${typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>`;
      });
      return html;
    }
  },
  legend: { show: false },
  grid: { top: 10, right: 10, bottom: 30, left: 40, containLabel: true },
  xAxis: {
    type: "category",
    data: timelineData.map(d => d.date),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#64748b", fontSize: 11, fontWeight: 600 },
    splitLine: { show: false },
  },
  yAxis: {
    type: "value",
    name: "Users",
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#64748b", fontSize: 11 },
    splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
  },
  series: [
    {
      name: "PID Users",
      type: "line",
      data: timelineData.map(d => d.pid),
      smooth: true,
      symbol: "none",
      lineStyle: { color: T.amber, width: 2.5 },
      // Soft fade to transparent
      areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.amber + "40" }, { offset: 1, color: T.amber + "00" }] } },
      emphasis: { focus: "series" },
    },
    {
      name: "Organic Users",
      type: "line",
      data: timelineData.map(d => d.organic),
      smooth: true,
      symbol: "none",
      lineStyle: { color: T.green, width: 2.5 },
      // Soft fade to transparent
      areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: T.green + "40" }, { offset: 1, color: T.green + "00" }] } },
      emphasis: { focus: "series" },
    },
  ],
});

const buildPieChartOption = (typeData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "item",
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [12, 16],
    formatter: (p) => `
      <div style="display:flex;align-items:center;gap:8px;">
        <span style="width:9px;height:9px;border-radius:50%;background:${p.color};display:inline-block;box-shadow:0 0 8px ${p.color};"></span>
        <span style="color:#94a3b8;font-size:12px;">${p.name}</span>
        <span style="color:#f1f5f9;font-size:13px;font-weight:800;">${p.value.toLocaleString()}</span>
        <span style="color:#64748b;font-size:11px;">(${p.percent.toFixed(1)}%)</span>
      </div>`,
  },
  legend: { show: false },
  series: [{
    type: "pie",
    radius: ["42%", "76%"],
    center: ["50%", "50%"],
    padAngle: 2,
    data: typeData.map((d, i) => ({
      name: d.name,
      value: d.value,
      itemStyle: {
        // Pure solid vibrant colors with clean white borders (No muddy shadows)
        color: d.name === "PID" ? T.amber : T.green,
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    })),
    label: {
      show: true,
      position: "inside",
      formatter: (p) => p.percent >= 5 ? `${p.percent.toFixed(0)}%` : "",
      fontSize: 12,
      fontWeight: 800,
      color: "#fff",
      textShadowBlur: 4,
      textShadowColor: "rgba(0,0,0,0.4)", // Clean light drop shadow for legibility
    },
    emphasis: {
      scale: true,
      scaleSize: 6,
      itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.15)" } // Soft outer drop shadow on hover
    },
    animationType: "expansion",
    animationDuration: 800,
  }],
});

const buildCampaignBarOption = (campaignData) => ({
  backgroundColor: "transparent",
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    backgroundColor: "rgba(15,23,42,0.97)",
    borderColor: "rgba(37,99,235,0.45)",
    borderWidth: 1,
    borderRadius: 14,
    padding: [12, 16],
    formatter: (params) => {
      const row = params[0];
      return `
        <div style="font-size:10px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#94a3b8;margin-bottom:8px;">${row.name}</div>
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="width:8px;height:8px;border-radius:50%;background:${row.color};box-shadow:0 0 6px ${row.color};display:inline-block;"></span>
          <span style="color:#94a3b8;font-size:12px;">Users</span>
          <span style="color:#f1f5f9;font-size:13px;font-weight:800;margin-left:auto;">${row.value.toLocaleString()}</span>
        </div>
      `;
    },
  },
  legend: { show: false },
  grid: { top: 8, right: 24, bottom: 32, left: 10, containLabel: true },
  xAxis: {
    type: "category",
    data: campaignData.map(d => d.campaign.length > 12 ? d.campaign.slice(0, 12) + "…" : d.campaign),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#475569", fontSize: 11, fontWeight: 700, rotate: -28, interval: 0 },
    splitLine: { show: false },
  },
  yAxis: {
    type: "value",
    name: "Users",
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: "#64748b", fontSize: 11 },
    splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
  },
  series: [{
    type: "bar",
    data: campaignData.map((d, i) => ({
      value: d.users,
      itemStyle: {
        // Clean, solid vibrant colors
        color: PALETTE[i % PALETTE.length],
        borderRadius: [6, 6, 0, 0],
      },
    })),
    barMaxWidth: 36,
    emphasis: { itemStyle: { color: T.blue } }, // Highlight blue on hover
  }],
});

// ════════════════════════════════════════════════════════════════════════════
const UserDashboard = () => {
  const defaultFilters = {
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    typeFilter: "",
    search: "",
  };

  const [tempFilters, setTempFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  const stats = {
    totalUsers: userData.length,
    pidUsers: userData.filter((u) => u.u_pid).length,
    organicUsers: userData.filter((u) => !u.u_pid).length,
  };

  const timelineData = buildTimelineData(userData);
  const typeData = buildTypeData(userData);
  const campaignData = buildCampaignData(userData);
  const { avgPerDay, peakDay } = buildDailyAvgData(userData);
  const hasData = !loading && userData.length > 0;

  useEffect(() => { fetchData(); }, [appliedFilters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const { startDate, endDate } = calculateDateRange(filters);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters.typeFilter) params.append("type", filters.typeFilter);
    if (filters.search) params.append("search", filters.search);
    return `?${params.toString()}`;
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await userDashboard(buildQueryParams(appliedFilters));
      if (response.status) { setUserData(response.data || []); }
      else { setError(response.message || "Failed to fetch data"); setUserData([]); }
    } catch (err) {
      setError("Network error. Please try again.");
      setUserData([]);
    } finally { setLoading(false); }
  };

  // ─── PDF Download ─────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    setShowDownloadMenu(false);
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("User Dashboard Report", 14, 15);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${formatDateTime(new Date(), "DD-MM-YYYY")} (IST)`, 14, 22);

    // Summary line
    doc.setFontSize(8);
    doc.text(
      `Total: ${stats.totalUsers}   PID: ${stats.pidUsers}   Organic: ${stats.organicUsers}`,
      14, 27
    );

    autoTable(doc, {
      startY: 32,
      head: [["Join Date", "Name", "Email", "PID", "Campaign", "Type"]],
      body: userData.map((u) => [
        formatDateTime(u.createdAt, "DD-MM-YYYY HH:mm:ss"),
        u.u_name || "N/A",
        u.u_email || "N/A",
        u.u_pid || "N/A",
        u.u_camp || "N/A",
        u.u_pid ? "PID" : "Organic",
      ]),
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        fontSize: 8,
      },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 35 },
        2: { cellWidth: 55 },
        3: { halign: "center", cellWidth: 30 },
        4: { cellWidth: 40 },
        5: { halign: "center", cellWidth: 22 },
      },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      margin: { left: 14, right: 14 },
    });

    doc.save(`User_Report_${formatDateTime(new Date(), "DD-MM-YYYY")}.pdf`);
  };

  // ─── Excel Download ────────────────────────────────────────────────────────
  const handleDownloadExcel = () => {
    setShowDownloadMenu(false);

    const wsData = [
      ["Join Date", "Name", "Email", "PID", "Campaign", "Type"],
      ...userData.map((u) => [
        formatDateTime(u.createdAt, "DD-MM-YYYY HH:mm:ss"),
        u.u_name || "N/A",
        u.u_email || "N/A",
        u.u_pid || "N/A",
        u.u_camp || "N/A",
        u.u_pid ? "PID" : "Organic",
      ]),
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [
      { wch: 22 }, { wch: 25 }, { wch: 35 },
      { wch: 18 }, { wch: 25 }, { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, "User Report");
    XLSX.writeFile(wb, `User_Report_${formatDateTime(new Date(), "DD-MM-YYYY")}.xlsx`);
  };

  const handleApplyFilters = () => setAppliedFilters({ ...tempFilters });
  const handleResetFilters = () => { setTempFilters(defaultFilters); setAppliedFilters(defaultFilters); };

  // ECharts options
  const areaOpt = hasData && timelineData.length > 0 ? buildAreaChartOption(timelineData) : null;
  const pieOpt = hasData && typeData.length > 0 ? buildPieChartOption(typeData) : null;
  const campaignOpt = hasData && campaignData.length > 0 ? buildCampaignBarOption(campaignData) : null;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .c-appear { animation: fadeUp 0.5s cubic-bezier(.22,.68,0,1.15) both; }
        .c-appear:nth-child(1){animation-delay:.06s}
        .c-appear:nth-child(2){animation-delay:.14s}
        .c-appear:nth-child(3){animation-delay:.22s}
        .c-appear:nth-child(4){animation-delay:.30s}
        .c-appear:nth-child(5){animation-delay:.38s}

        .f-select {
          width:100%; padding:11px 14px;
          border:2px solid #e5e7eb; border-radius:12px;
          background:#fafbff; color:#111827;
          font-size:13px; font-weight:600; outline:none;
          transition:border-color 0.18s, box-shadow 0.18s;
          appearance:none; cursor:pointer;
        }
        .f-select:focus { border-color:#2563EB; box-shadow:0 0 0 4px rgba(37,99,235,0.1); }
        .f-label {
          display:block; font-size:10px; font-weight:800;
          letter-spacing:0.1em; text-transform:uppercase;
          color:#9ca3af; margin-bottom:7px;
        }
        .btn-primary {
          display:inline-flex; align-items:center; gap:9px;
          padding:11px 20px; border-radius:12px; border:none; cursor:pointer;
          font-size:13.5px; font-weight:800; color:#fff;
          background:linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
          box-shadow:0 4px 16px rgba(37,99,235,0.32);
          transition:transform 0.15s, box-shadow 0.15s;
        }
        .btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(37,99,235,0.42); }
        .btn-green {
          display:inline-flex; align-items:center; gap:9px;
          padding:11px 20px; border-radius:12px; border:none; cursor:pointer;
          font-size:13.5px; font-weight:800; color:#fff;
          background:linear-gradient(135deg, #10B981 0%, #059669 100%);
          box-shadow:0 4px 16px rgba(16,185,129,0.28);
          transition:transform 0.15s, box-shadow 0.15s;
        }
        .btn-green:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(16,185,129,0.38); }
        .btn-ghost {
          display:inline-flex; align-items:center; gap:8px;
          padding:11px 18px; border-radius:12px; cursor:pointer;
          font-size:13px; font-weight:700; color:#6b7280;
          background:#f9fafb; border:2px solid #e5e7eb;
          transition:all 0.15s;
        }
        .btn-ghost:hover { background:#f3f4f6; color:#374151; border-color:#d1d5db; }
        .dl-dropdown {
          position:absolute; top:calc(100% + 8px); right:0; z-index:50;
          background:#fff; border:1px solid #e5e7eb; border-radius:16px;
          box-shadow:0 20px 60px rgba(0,0,0,0.12),0 4px 16px rgba(0,0,0,0.06);
          overflow:hidden; min-width:196px;
          animation: fadeUp 0.18s ease both;
        }
        .dl-item {
          display:flex; align-items:center; gap:10px;
          padding:13px 18px; font-size:13px; font-weight:700;
          color:#374151; cursor:pointer;
          transition:background 0.12s,color 0.12s;
          border-bottom:1px solid #f9fafb;
        }
        .dl-item:last-child { border-bottom:none; }
        .dl-item.pdf:hover   { color:#ef4444; background:#fff5f5; }
        .dl-item.excel:hover { color:#16a34a; background:#f0fdf4; }
      `}</style>

      <div style={{ marginBottom: 40, fontFamily: "system-ui, -apple-system, sans-serif" }}>

        {/* ══ HEADER ════════════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 20, marginBottom: 28, position: "relative", zIndex: 10 }} className="c-appear">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ padding: 12, borderRadius: 18, background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)", boxShadow: "0 6px 20px rgba(37,99,235,0.38)" }}>
              <FaUsers style={{ fontSize: 24, color: "#fff" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.03em", lineHeight: 1 }}>User Dashboard</h1>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>Monitor and analyze user registrations</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative" }} ref={downloadMenuRef}>
              <button className="btn-green" onClick={() => setShowDownloadMenu(p => !p)}>
                <FaDownload style={{ fontSize: 13 }} /> Download
                <FaChevronDown style={{ fontSize: 11, transition: "transform 0.2s", transform: showDownloadMenu ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>
              {showDownloadMenu && (
                <div className="dl-dropdown">
                  <div className="dl-item pdf" onClick={handleDownloadPDF}><FaFilePdf style={{ fontSize: 14 }} /> Download PDF</div>
                  <div className="dl-item excel" onClick={handleDownloadExcel}><FaFileExcel style={{ fontSize: 14 }} /> Download Excel</div>
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={fetchData}>
              <MdRefresh style={{ fontSize: 16, animation: loading ? "spin 0.8s linear infinite" : "none" }} /> Refresh
            </button>
          </div>
        </div>

        {/* ══ STAT CARDS ════════════════════════════════════════════════════════ */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 28 }} className="c-appear">
          <StatCard label="Total Users" value={stats.totalUsers} icon="👥" gradient="linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)" glow="#2563eb" />
          <StatCard label="PID Users" value={stats.pidUsers} icon="🎯" gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" glow="#f59e0b" />
          <StatCard label="Organic Users" value={stats.organicUsers} icon="🌿" gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)" glow="#10b981" />
        </div>

        {/* ══ FILTERS ════════════════════════════════════════════════════════════ */}
        <div style={{ background: "#fff", borderRadius: 24, border: "1px solid #f1f5f9", padding: "24px 28px", marginBottom: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.04),0 12px 40px rgba(0,0,0,0.06)" }} className="c-appear">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ padding: "9px 10px", borderRadius: 12, background: "#eff6ff", border: "1px solid #dbeafe" }}>
              <FaFilter style={{ color: T.blue, fontSize: 11 }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.01em" }}>Filters</h2>
              <p style={{ margin: "3px 0 0", fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>Narrow results by date and type</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <label className="f-label">📅 Date Range</label>
              <select className="f-select" value={tempFilters.dateRangeType}
                onChange={e => setTempFilters({ ...tempFilters, dateRangeType: e.target.value })}>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            <div>
              <label className="f-label">🔗 Type</label>
              <select className="f-select" value={tempFilters.typeFilter}
                onChange={e => setTempFilters({ ...tempFilters, typeFilter: e.target.value })}>
                <option value="">All Types</option>
                <option value="organic">Organic</option>
                <option value="pid">PID</option>
              </select>
            </div>
          </div>

          {tempFilters.dateRangeType === "custom" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16, padding: 16, background: "#f8fafc", borderRadius: 16, border: "1px solid #f1f5f9" }}>
              <div>
                <label className="f-label">Start Date</label>
                <input type="date" className="f-select" value={tempFilters.customStartDate}
                  onChange={e => setTempFilters({ ...tempFilters, customStartDate: e.target.value })} />
              </div>
              <div>
                <label className="f-label">End Date</label>
                <input type="date" className="f-select" value={tempFilters.customEndDate}
                  onChange={e => setTempFilters({ ...tempFilters, customEndDate: e.target.value })} />
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
            <button className="btn-primary" onClick={handleApplyFilters}><FaFilter style={{ fontSize: 11 }} /> Apply Filters</button>
            <button className="btn-ghost" onClick={handleResetFilters}><FaSync style={{ fontSize: 11 }} /> Reset</button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ marginBottom: 20, padding: "16px 20px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <p style={{ margin: 0, color: "#e11d48", fontWeight: 600, fontSize: 13 }}>{error}</p>
          </div>
        )}

        {/* ══ CHARTS ══════════════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SectionLabel>User Analytics</SectionLabel>

          {/* ── CHART 1: Area Chart ───────────────────────────────────────────── */}
          <div className="c-appear" style={{
            background: "#fff", borderRadius: 28, overflow: "hidden", position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04),0 12px 40px rgba(0,0,0,0.08),0 0 0 1px rgba(0,0,0,0.04)",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#2563EB,#F59E0B,#10B981)" }} />
            <div style={{ position: "absolute", top: 0, right: 0, width: 340, height: 260, pointerEvents: "none", background: "radial-gradient(circle at 80% 0%,#2563EB0d 0%,transparent 60%)" }} />

            <div style={{ position: "relative", zIndex: 1, padding: "26px 28px 22px" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ padding: "9px 10px", borderRadius: 13, background: "#2563EB14", border: "1px solid #2563EB22", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>📊</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.02em" }}>User Registrations Over Time</h3>
                    <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "#94a3b8", fontWeight: 500 }}>Daily PID vs Organic signups</p>
                  </div>
                </div>
                {/* Pill legend */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <LegendPill color={T.amber} label="PID Users" />
                  <LegendPill color={T.green} label="Organic Users" />
                </div>
              </div>

              {/* Mini stat chips */}
              {hasData && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
                  <MiniChip
                    label="Daily Average"
                    value={avgPerDay.toLocaleString()}
                    color={T.indigo}
                    bg="#eef2ff"
                    border="#c7d2fe"
                  />
                  <MiniChip
                    label="Peak Day"
                    value={peakDay?.date || "—"}
                    color={T.blue}
                    bg="#dbeafe"
                    border="#bfdbfe"
                  />
                  <MiniChip
                    label="Peak Count"
                    value={peakDay?.total.toLocaleString() || "—"}
                    color={T.violet}
                    bg="#ede9fe"
                    border="#ddd6fe"
                  />
                </div>
              )}

              {areaOpt
                ? <ReactECharts option={areaOpt} style={{ height: 300, width: "100%" }} notMerge={true} lazyUpdate={true} />
                : <ChartEmpty loading={loading} height={300} message="No registration data for this period" />
              }
            </div>
          </div>

          {/* ── CHART 2 + CHART 3 (side by side) ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 20 }} className="c-appear">
            <ChartCard title="User Type Distribution" subtitle="PID vs Organic split" accent={T.amber} icon="🍩">
              {pieOpt ? (
                <>
                  <ReactECharts option={pieOpt} style={{ height: 240, width: "100%" }} notMerge={true} lazyUpdate={true} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 6 }}>
                    {typeData.map((entry, i) => (
                      <LegendPill
                        key={i}
                        color={entry.name === "PID" ? T.amber : T.green}
                        label={`${entry.name} · ${entry.value.toLocaleString()}`}
                      />
                    ))}
                  </div>
                </>
              ) : <ChartEmpty loading={loading} />}
            </ChartCard>

            <ChartCard title="Top Campaigns" subtitle="Best performing campaigns by user count" accent={T.violet} icon="🚀">
              {campaignOpt ? (
                <>
                  <ReactECharts option={campaignOpt} style={{ height: 280, width: "100%" }} notMerge={true} lazyUpdate={true} />
                  {campaignData.length > 0 && (
                    <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
                      <LegendPill color={T.violet} label={`${campaignData.length} active campaigns`} />
                    </div>
                  )}
                </>
              ) : <ChartEmpty loading={loading} height={280} message="No campaign data available" />}
            </ChartCard>
          </div>

          {/* Quick Stats Row */}
          {hasData && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
              gap: 16,
              marginTop: 8
            }} className="c-appear">
              <div style={{
                background: "#fff",
                borderRadius: 20,
                padding: "18px 22px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
                border: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: 16
              }}>
                <div style={{ padding: 12, borderRadius: 14, background: `${T.blue}14` }}>
                  <FaUserPlus style={{ fontSize: 20, color: T.blue }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>PID Conversion</p>
                  <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 900, color: "#1e293b" }}>
                    {stats.totalUsers > 0 ? Math.round((stats.pidUsers / stats.totalUsers) * 100) : 0}%
                  </p>
                </div>
              </div>
              <div style={{
                background: "#fff",
                borderRadius: 20,
                padding: "18px 22px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
                border: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: 16
              }}>
                <div style={{ padding: 12, borderRadius: 14, background: `${T.green}14` }}>
                  <FaLeaf style={{ fontSize: 20, color: T.green }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Organic Share</p>
                  <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 900, color: "#1e293b" }}>
                    {stats.totalUsers > 0 ? Math.round((stats.organicUsers / stats.totalUsers) * 100) : 0}%
                  </p>
                </div>
              </div>
              <div style={{
                background: "#fff",
                borderRadius: 20,
                padding: "18px 22px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
                border: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: 16
              }}>
                <div style={{ padding: 12, borderRadius: 14, background: `${T.amber}14` }}>
                  <FaCalendarAlt style={{ fontSize: 20, color: T.amber }} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Total Days</p>
                  <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 900, color: "#1e293b" }}>
                    {timelineData.length}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserDashboard;

